/**
 * onboardingView.ts — pure view-model helpers for HR / Onboarding (ON-2).
 *
 * Progress figures come from the SERVER's derived doneCount/itemCount (the
 * pin-3 no-status-column derivation) — nothing here recounts items, so the
 * numbers cannot drift from what the aggregate query said. Dates follow the
 * sopsView en-GB long form.
 */
import type { Onboarding, OnboardingTemplate } from '../../types'

/** en-GB long date ("15 July 2026") — the sopsView/mockup formatCalDate. */
export function formatOnboardingDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** "3 of 5 done" — the server-derived progress pair, verbatim. */
export function progressLine(o: Pick<Onboarding, 'doneCount' | 'itemCount'>): string {
  return `${o.doneCount} of ${o.itemCount} done`
}

/** Complete = every item done (pin 3: derived, never stored). An empty
 *  checklist cannot exist (the start path refuses a template with no items),
 *  but guard anyway — zero items is NOT complete. */
export function isOnboardingComplete(o: Pick<Onboarding, 'doneCount' | 'itemCount'>): boolean {
  return o.itemCount > 0 && o.doneCount === o.itemCount
}

/** 0-100 width for the progress bar; 0 when the checklist is empty. */
export function progressPct(o: Pick<Onboarding, 'doneCount' | 'itemCount'>): number {
  if (o.itemCount <= 0) return 0
  return Math.round((o.doneCount / o.itemCount) * 100)
}

/** The header count line: in-progress vs complete, from server-derived
 *  figures ("2 in progress · 1 complete"). */
export function onboardingCountLine(list: Pick<Onboarding, 'doneCount' | 'itemCount'>[]): string {
  const complete = list.filter(isOnboardingComplete).length
  const inProgress = list.length - complete
  return `${inProgress} in progress · ${complete} complete`
}

/** Instance card meta: "Client Onboarding · Started 15 July 2026". */
export function onboardingMeta(o: Pick<Onboarding, 'templateTitle' | 'startedAt'>): string {
  return `${o.templateTitle} · Started ${formatOnboardingDate(o.startedAt)}`
}

/** Template card meta: "5 items · Updated 15 July 2026". */
export function templateMeta(t: Pick<OnboardingTemplate, 'items' | 'updatedAt'>): string {
  const n = t.items.length
  return `${n} item${n !== 1 ? 's' : ''} · Updated ${formatOnboardingDate(t.updatedAt)}`
}

/** Template editor round-trip: the textarea holds one item per line (the
 *  SOPs numbered-steps idiom); line order IS the order — the server assigns
 *  positions 1..n from the array. Blank lines are dropped, titles trimmed. */
export function linesToItems(text: string): { title: string }[] {
  return text
    .split('\n')
    .map(l => l.trim())
    .filter(l => l !== '')
    .map(title => ({ title }))
}

export function itemsToLines(items: { title: string }[]): string {
  return items.map(i => i.title).join('\n')
}
